<template>
  <div>
    <v-card class="pa-6 overflow-x-auto light-scrollbar">
      <div class="d-flex flex-column gap-4 line-height-normal" style="min-width: 800px">
        <div v-for="userEntry in projectUsers" :key="userEntry.key" class="hovervisible-activator d-flex align-center gap-4" style="position: relative">
          <div class="flex-grow-1" style="flex-basis: 40%">
            <user-avatar :user="userEntry.user" show-username size="32" class="mr-2"></user-avatar>
          </div>

          <div v-for="statData in statDataItems" :key="statData.id" style="flex-basis: 20%">
            <div class="d-flex align-center flex-grow-1">
              <v-icon size="small" class="text-medium-emphasis mr-1">
                {{ statData.icon }}
              </v-icon>
              <div v-if="userEntry[statData.id].total" class="rounded overflow-hidden d-flex flex-grow-1" style="height: 10px">
                <div v-for="(stat, id) in userEntry[statData.id].items" :key="id" :title="userStatTitle(statData.id, stat)"
                     :class="stat.class" :style="{ width: `${stat.percent}%` }">
                </div>
              </div>
              <div v-else class="text-medium-emphasis small nobr">{{ $t(statData.noneText) }}</div>
            </div>
            <span v-if="userEntry[statData.id].total" class="ml-6 small">{{ $t(statData.countText, countTextParams(statData.id, userEntry[statData.id])) }}</span>
          </div>

          <v-btn v-if="isBusiness" :title="$t('PROJECT_SHEET.REPLACE_USER_IN_PROJECT')" icon size="small" density="comfortable" color="primary" class="hovervisible-item"
                 @click="userToReplace = userEntry.user">
            <v-icon>far fa-exchange</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>

    <div ref="replaceuser">
      <v-card v-if="isBusiness && userToReplace" class="mt-4 pa-6 export-hidden">
        <div class="d-flex justify-space-between align-center">
          <b>{{ $t('PROJECT_SHEET.REPLACEMENT_USERS') }}</b>
          <v-btn-icon size="small" @click="userToReplace = null">
            <v-icon size="large">far fa-times</v-icon>
          </v-btn-icon>
        </div>
        <div>
          <span class="small text-medium-emphasis">{{ $t('PROJECT_SHEET.REPLACE_USER_DESC') }}</span>
          <div class="d-flex align-center mt-2">
            <v-icon style="flex: 0 0 16px; margin-right: 9px">far fa-user</v-icon>
            <span style="flex: 0 1 211px">{{ $t('MANAGE.CONFIG.REPLACE_USER.REPLACE') }}</span>
            <div class="py-1 px-2 bg-background rounded">
              <template v-if="userToReplace.id">
                <img v-if="getUserById(userToReplace.id) && userToReplace.avatar" :src="userToReplace.avatar" class="rounded-circle" style="max-height: 20px">
                <v-icon v-else :title="$t('MULTIPROJECTS.DELETED_USER', 1)" size="small" class="color-inherit">far fa-user-slash</v-icon>
              </template>
              <template v-else-if="userToReplace.group_id">
                <v-icon v-if="getGroupById(userToReplace.group_id)" size="small" class="color-inherit">far fa-users</v-icon>
                <v-icon v-else :title="$t('MULTIPROJECTS.DELETED_GROUP')" size="small" class="color-inherit">far fa-users-slash</v-icon>
              </template>
              <v-icon v-else size="small" class="color-inherit">far fa-user</v-icon>
              <span class="ml-1">{{ userToReplace.group_id ? userToReplace.title : $filters.username(userToReplace) }}</span>
            </div>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-select v-model="replaceBy.type" :items="replaceOptions" :label="$t('MANAGE.CONFIG.REPLACE_USER.BY')" prepend-icon="far fa-arrow-right"
                      class="mr-4" style="max-width: 220px">
            </v-select>
            <v-text-field v-if="replaceBy.type == 'username'" v-model="replaceBy.username" autocomplete="nope">
            </v-text-field>
            <v-select v-else-if="replaceBy.type == 'user'" v-model="replaceBy.user" :items="organizationUsers.filter(item => item.id != userToReplace.id)"
                      :item-title="$filters.username" item-value="id" :menu-props="{ location: 'bottom' }">
            </v-select>
            <v-select v-else-if="replaceBy.type == 'group'" v-model="replaceBy.group" :items="companyGroups.filter(item => item.id != userToReplace.group_id)"
                      item-title="title" item-value="id" :menu-props="{ location: 'bottom' }">
            </v-select>
          </div>
          <div class="text-center mt-4">
            <v-btn :disabled="! btnEnabled" :loading="replaceUserInProgress" rounded size="large" class="mx-2" color="primary" @click="replaceUser">
              {{ $t('MANAGE.CONFIG.REPLACE_USER.REPLACE_IN_PROJECTS', 1) }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
  import { buildActionStats, buildBubbleStats, buildWorkloadStats } from '@/components/ProjectSheet/activityHelpers';
  import useUiMsgboxStore from '@/stores/ui/msgbox';
  import useUsersStore from '@/stores/users';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import useUsersGroupsStore from '@/stores/users/groups';

  export default {
    props: {
      planning: { type: Object, required: true },
    },
    emits: ['reload-planning'],
    data() {
      return {
        statDataItems: [
          { id: 'actionStats', icon: 'far fa-check-square', countText: 'GLOBAL.ACTIONS_COUNT', noneText: 'PROJECT_SHEET.NO_ACTION' },
          { id: 'workloadStats', icon: 'far fa-weight-hanging', countText: 'helpers.workloadCount', noneText: 'PROJECT_SHEET.NO_WORKLOAD' },
          { id: 'bubbleStats', icon: 'fas fa-long-arrow-right', countText: 'GLOBAL.BUBBLES_COUNT', noneText: 'PROJECT_SHEET.NO_BUBBLE' },
        ],
        userToReplace: null,
        replaceBy: {
          type: 'user',
          user: null,
          group: null,
          username: '',
        },
        replaceOptions: [
          { value: 'username', title: this.$t('MANAGE.CONFIG.REPLACE_USER.VIRTUAL_PARTICIPANT', 1) },
          { value: 'user', title: this.$t('MANAGE.CONFIG.REPLACE_USER.REGISTERED_USER') },
          { value: 'group', title: this.$t('GLOBAL.GROUP') },
          { value: 'remove', title: this.$t('MANAGE.CONFIG.REPLACE_USER.REMOVE_USER') },
        ],
        replaceUserInProgress: false,
        uiMsgboxStore: useUiMsgboxStore(),
        usersStore: useUsersStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
        usersGroupsStore: useUsersGroupsStore(),
      };
    },
    computed: {
      projectUsers() {
        const users = {};
        this.planning.elements.forEach((item) => {
          if (! item.getUsers()) return;
          item.getUsers().forEach((user) => {
            const key = this.getUserKey(user);
            if (key) users[key] = user;
          });
        });
        return Object.entries(users).map(([key, user]) => {
          const userKey = this.getUserKey(user);
          const elements = this.planning.elements.filter(el => !! (el.getUsers() || []).find(item => this.getUserKey(item) == userKey));
          return {
            key,
            user,
            actionStats: buildActionStats(elements),
            workloadStats: buildWorkloadStats(elements),
            bubbleStats: buildBubbleStats(elements),
          };
        });
      },
      getUserById() {
        return this.usersStore.getUserById;
      },
      getGroupById() {
        return this.usersGroupsStore.getGroupById;
      },
      organizationUsers() {
        return this.usersStore.getOrganizationUsers;
      },
      companyGroups() {
        return this.usersGroupsStore.companyGroups;
      },
      btnEnabled() {
        return this.userToReplace && (this.replaceBy.type == 'remove' || this.replaceBy[this.replaceBy.type]);
      },
      workloadInHours() {
        return this.usersAccessRightStore.config.workloadInHours;
      },
      isBusiness() { return this.usersAccessRightStore.isBusiness; },
    },
    watch: {
      userToReplace(newVal) {
        if (newVal) this.$nextTick(() => { this.$refs.replaceuser?.scrollIntoView({ behavior: 'smooth' }); });
      },
    },
    methods: {
      getUserKey(user) {
        return user?.id || (user?.group_id && `group${user.group_id}`) || user?.username || user?.email || null;
      },
      countTextParams(statDataId, stat) {
        if (statDataId == 'workloadStats') {
          return { count: Math.round(stat.total), value: stat.total, workloadInHours: this.workloadInHours };
        }
        return { count: Math.round(stat.total) };
      },
      userStatTitle(statDataId, stat) {
        let unit = '';
        if (statDataId == 'workloadStats') unit = this.$t('helpers.workloadShort', { workloadInHours: this.workloadInHours });
        return `${stat.number}${unit} ${this.$t(stat.label)}`;
      },
      replaceUser() {
        if (! this.btnEnabled) return;
        this.replaceUserInProgress = true;
        window.apiSrv.call('plannings/tasks/massreplaceusers', 'store', {
          planning_ids: [this.planning.id],
          replace_type: this.userToReplace.id ? 'user' : (this.userToReplace.group_id ? 'group' : 'username'),
          replace_user_id: this.userToReplace.id || this.userToReplace.group_id || this.userToReplace.username,
          replace_by_type: this.replaceBy.type,
          replace_by_user_id: this.replaceBy[this.replaceBy.type],
        }).then(() => {
          this.userToReplace = null;
          this.replaceBy.user = null;
          this.replaceBy.group = null;
          this.replaceBy.username = '';

          this.$emit('reload-planning');
        }).catch((message) => {
          if (message) this.uiMsgboxStore.open({ title: this.$t('PROJECT_SHEET.REPLACE_USER_ERROR_MESSAGE'), body: message || '' });
        }).then(() => {
          this.replaceUserInProgress = false;
        });
      },
    },
  };
</script>
